import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ExternalLink, Video } from 'lucide-react';
import { DyteMeeting } from '@dytesdk/react-ui-kit';
import { useDyteClient } from '@dytesdk/react-web-core';
import './TelehealthCard.css';

const dyteAuthToken = process.env.REACT_APP_DYTE_AUTH_TOKEN;
const dyteMeetingUrl = process.env.REACT_APP_DYTE_MEETING_URL;

const TelehealthCard = () => {
  const [meeting, initMeeting] = useDyteClient();
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  const hasMeetingUrl = Boolean(dyteMeetingUrl);
  const hasAuthToken = Boolean(dyteAuthToken);

  useEffect(() => {
    let mounted = true;

    const connectMeeting = async () => {
      // If the app is configured with a hosted Dyte URL, use iframe mode instead of SDK init.
      if (!hasAuthToken || hasMeetingUrl) return;

      setIsJoining(true);
      setJoinError('');

      try {
        await initMeeting({
          authToken: dyteAuthToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
      } catch (error) {
        if (mounted) {
          setJoinError('Could not connect to Dyte. Check REACT_APP_DYTE_AUTH_TOKEN and try again.');
        }
      } finally {
        if (mounted) {
          setIsJoining(false);
        }
      }
    };

    connectMeeting();

    return () => {
      mounted = false;
    };
  }, [hasAuthToken, hasMeetingUrl, initMeeting]);

  const setupMessage = useMemo(() => {
    if (hasMeetingUrl) {
      return 'Dyte meeting URL detected. Launching embedded telehealth room.';
    }

    if (hasAuthToken) {
      return 'Dyte auth token detected. Connecting via Dyte SDK.';
    }

    return 'Add REACT_APP_DYTE_AUTH_TOKEN or REACT_APP_DYTE_MEETING_URL in your environment file to enable live calls.';
  }, [hasAuthToken, hasMeetingUrl]);

  return (
    <div className="telehealth-container">
      <div className="telehealth-header">
        <h2>Telehealth Video Calls (Dyte)</h2>
        <p>{setupMessage}</p>
      </div>

      <div className="telehealth-call telehealth-live">
        {hasMeetingUrl && (
          <div className="telehealth-iframe-wrap">
            <iframe
              title="Dyte Meeting"
              src={dyteMeetingUrl}
              className="telehealth-iframe"
              allow="camera; microphone; fullscreen; display-capture; autoplay"
            />
            <a className="telehealth-open-link" href={dyteMeetingUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={16} /> Open meeting in a new tab
            </a>
          </div>
        )}

        {!hasMeetingUrl && hasAuthToken && (
          <div className="telehealth-sdk-wrap">
            {isJoining && !meeting && (
              <div className="telehealth-state-card">
                <Video size={34} />
                <p>Connecting to Dyte meeting...</p>
              </div>
            )}

            {joinError && (
              <div className="telehealth-alert" role="alert">
                <AlertCircle size={16} />
                <span>{joinError}</span>
              </div>
            )}

            {meeting && (
              <div className="telehealth-dyte-root">
                <DyteMeeting mode="fill" meeting={meeting} showSetupScreen />
              </div>
            )}
          </div>
        )}

        {!hasMeetingUrl && !hasAuthToken && (
          <div className="telehealth-state-card">
            <Video size={34} />
            <p>Dyte is not configured yet.</p>
            <small>
              Set <code>REACT_APP_DYTE_AUTH_TOKEN</code> or <code>REACT_APP_DYTE_MEETING_URL</code> and restart the app.
            </small>
          </div>
        )}
      </div>

      <div className="telehealth-features">
        <div className="feature-card">
          <h4>Environment Keys</h4>
          <div className="history-list">
            <div className="history-item">
              <span className="history-date">Required</span>
              <span className="history-patient">REACT_APP_DYTE_AUTH_TOKEN</span>
              <span className="history-duration">SDK</span>
            </div>
            <div className="history-item">
              <span className="history-date">Alternative</span>
              <span className="history-patient">REACT_APP_DYTE_MEETING_URL</span>
              <span className="history-duration">Iframe</span>
            </div>
          </div>
        </div>

        <div className="feature-card">
          <h4>Setup Notes</h4>
          <div className="quick-actions">
            <p className="call-time">Restart the dev server after changing environment values.</p>
            <p className="call-time">Use meeting URL mode for hosted rooms, or auth token mode for SDK controls.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelehealthCard;
